import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Button, Stack, Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifyError } from '../lib/notify';
import { authApi, type LoginInput } from '../api/auth';
import { ApiCallError, setOnSessionExpired, setOnSessionKilled, type SessionKillReason } from '../api/client';
import { useTranslation } from '../i18n/LocaleContext';
import type { TranslationKey } from '../i18n/translations';
import { useSessionStream } from './useSessionStream';
import { useTabGuard } from './useTabGuard';
import type { Me, Profile } from '../api/types';

/**
 * Reason -> blocking-popup i18n keys (literal so they typecheck against the
 * TranslationKey union). Values must exist in both locale maps in translations.ts.
 */
const KILL_TEXT: Record<SessionKillReason, { title: TranslationKey; body: TranslationKey }> = {
  superseded: { title: 'auth.sessionKilled.superseded.title', body: 'auth.sessionKilled.superseded.body' },
  ip_changed: { title: 'auth.sessionKilled.ipChanged.title', body: 'auth.sessionKilled.ipChanged.body' },
  concurrent_tab: { title: 'auth.sessionKilled.concurrentTab.title', body: 'auth.sessionKilled.concurrentTab.body' },
  logged_out: { title: 'auth.sessionKilled.loggedOut.title', body: 'auth.sessionKilled.loggedOut.body' },
  suspended: { title: 'auth.sessionKilled.suspended.title', body: 'auth.sessionKilled.suspended.body' },
};

type SessionState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; profile: Profile; me: Me | null };

type SessionContextValue = {
  state: SessionState;
  login: (input: LoginInput) => Promise<Profile>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const SessionContext = createContext<SessionContextValue | null>(null);

const STORAGE_KEY = 'transpay_profile_cache';

/**
 * Session state is HttpOnly-cookie based on the server, but we cache the
 * profile in localStorage so first paint doesn't show a flash of unauth.
 * The cache is purely a hint — server cookie is the source of truth.
 */
function readCachedProfile(): Profile | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Profile;
  } catch {
    return null;
  }
}

function writeCachedProfile(profile: Profile | null): void {
  if (profile) localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  else localStorage.removeItem(STORAGE_KEY);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>(() => {
    const cached = readCachedProfile();
    return cached
      ? { status: 'authenticated', profile: cached, me: null }
      : { status: 'loading' };
  });

  const { t } = useTranslation();

  // Mirror state into a ref so the module-level API error handler below can
  // read the *current* status without capturing a stale closure.
  const stateRef = useRef(state);
  stateRef.current = state;

  // Global session-expiry guard. Any API call that comes back with a
  // "session dead" 401 (cookie gone or JWT expired mid-session) trips the
  // handler registered in api/client. We only react when we currently believe
  // we're authenticated: a 401 during first-load bootstrap (a visitor who
  // simply isn't logged in yet) is handled by the mount effect below and must
  // NOT raise a misleading "session expired" toast.
  useEffect(() => {
    setOnSessionExpired(() => {
      if (stateRef.current.status !== 'authenticated') return;
      writeCachedProfile(null);
      setState({ status: 'unauthenticated' });
      // Consistent global error toast; fixed id dedupes concurrent 401s.
      notifyError(t('auth.sessionExpired'), t('auth.sessionExpiredTitle'), 'session-expired');
    });
    return () => setOnSessionExpired(null);
  }, [t]);

  // --- Single-session forced logout (FR-1 tabs / FR-2 other device / FR-3 IP) ---
  // One BLOCKING popup with a reason-specific message, then a hard redirect to
  // /login. The module-scoped killingRef + the authenticated-status guard prevent
  // duplicate modals when the SSE push, the multi-tab broadcast, and the next
  // 401 all fire at once. Driven by: api/client 401 (registered below),
  // useSessionStream (SSE), and useTabGuard (BroadcastChannel).
  const killingRef = useRef(false);
  const killSession = useCallback(
    (reason: SessionKillReason) => {
      if (stateRef.current.status !== 'authenticated') return;
      if (killingRef.current) return;
      killingRef.current = true;

      // A same-browser 2nd tab shares the still-valid cookie — denylist it so
      // re-login is mandatory. Other reasons are already dead server-side.
      if (reason === 'concurrent_tab') {
        void authApi.logout().catch(() => undefined);
      }
      writeCachedProfile(null);
      setState({ status: 'unauthenticated' });

      const { title, body } = KILL_TEXT[reason];
      modals.open({
        modalId: 'session-killed',
        withCloseButton: false,
        closeOnClickOutside: false,
        closeOnEscape: false,
        title: t(title),
        children: (
          <Stack>
            <Text>{t(body)}</Text>
            <Button
              fullWidth
              onClick={() => {
                modals.closeAll();
                window.location.assign('/login');
              }}
            >
              {t('auth.sessionKilled.button')}
            </Button>
          </Stack>
        ),
      });
    },
    [t],
  );

  // Register the forced-logout handler for the API client's 401 backstop path.
  useEffect(() => {
    setOnSessionKilled(killSession);
    return () => setOnSessionKilled(null);
  }, [killSession]);

  // Instant push (cross-device, FR-2/FR-3) + same-browser multi-tab guard
  // (FR-1) — active ONLY while authenticated so first-load bootstrap can't fire
  // a false popup.
  const authed = state.status === 'authenticated';
  useSessionStream(authed, killSession);
  useTabGuard(authed, killSession);

  // Fetch fresh /api/auth/me into the authenticated state. Permissions are
  // refreshed every load so role-edit propagates without a re-login.
  const hydrateMe = useCallback(async () => {
    try {
      const me = await authApi.me();
      const profile: Profile = {
        id: me.id,
        email: me.email,
        accountName: me.accountName,
        userType: me.userType,
        merchantId: me.merchantId,
        isSuperuser: me.isSuperuser,
      };
      writeCachedProfile(profile);
      setState({ status: 'authenticated', profile, me });
    } catch (err) {
      if (err instanceof ApiCallError && err.status === 401) {
        writeCachedProfile(null);
        setState({ status: 'unauthenticated' });
      }
      throw err;
    }
  }, []);

  // On mount, verify the cookie by calling /me. If the cookie is gone or
  // expired, the server returns 401 and we transition to unauthenticated.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await authApi.refresh();
        if (cancelled) return;
        await hydrateMe();
      } catch {
        if (!cancelled) {
          writeCachedProfile(null);
          setState({ status: 'unauthenticated' });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hydrateMe]);

  const login = useCallback(async (input: LoginInput) => {
    const resp = await authApi.login(input);
    writeCachedProfile(resp.profile);
    setState({ status: 'authenticated', profile: resp.profile, me: null });
    // Hydrate permissions in the background; UI can render with the basic
    // profile immediately and gates kick in once me resolves.
    void hydrateMe().catch(() => undefined);
    return resp.profile;
  }, [hydrateMe]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // already gone, fine
    }
    writeCachedProfile(null);
    setState({ status: 'unauthenticated' });
  }, []);

  const refresh = useCallback(async () => {
    try {
      await authApi.refresh();
    } catch (err) {
      if (err instanceof ApiCallError && err.status === 401) {
        writeCachedProfile(null);
        setState({ status: 'unauthenticated' });
      }
      throw err;
    }
  }, []);

  return (
    <SessionContext.Provider value={{ state, login, logout, refresh }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

export function useProfile(): Profile {
  const { state } = useSession();
  if (state.status !== 'authenticated') {
    throw new Error('useProfile called outside an authenticated route');
  }
  return state.profile;
}

/**
 * Hook returning the current viewer's permission set.
 *
 *  - `permissions === null` means superuser (god-mode).
 *  - Empty array means the resolver hasn't returned yet OR the user genuinely
 *    has no role; treat as "no access" while loading.
 *
 * Returns `{ has(key), loading, isSuperuser, me }` for ergonomic use:
 *
 *   const { has, isSuperuser } = usePermissions();
 *   if (!has('users.manage') && !isSuperuser) return null;
 */
export function usePermissions(): {
  has: (key: string) => boolean;
  isSuperuser: boolean;
  loading: boolean;
  me: Me | null;
} {
  const { state } = useSession();
  if (state.status !== 'authenticated') {
    return {
      has: () => false,
      isSuperuser: false,
      loading: state.status === 'loading',
      me: null,
    };
  }
  const me = state.me;
  return {
    me,
    loading: me === null,
    isSuperuser: state.profile.isSuperuser,
    has: (key: string) => {
      if (state.profile.isSuperuser) return true;
      if (me === null) return false;
      if (me.permissions === null) return true;
      return me.permissions.includes(key);
    },
  };
}
